import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { User, LogOut, Award, Trophy, Zap, Crown, Copy, Share2, Quote, Save } from "lucide-react";

const BADGE_META = {
  "voce-noua": { name: "Voce nouă", emoji: "🌱", color: "from-[#3BA76D] to-[#2D8557]" },
  "sustinator": { name: "Susținător", emoji: "💪", color: "from-[#5FA0E8] to-[#3F7EC8]" },
  "ambasador": { name: "Ambasador", emoji: "⭐", color: "from-[#E89BB8] to-[#D4748E]" },
  "lider-comunitate": { name: "Lider de comunitate", emoji: "👑", color: "from-[#F2C053] to-[#D4A63D]" },
  "fondator": { name: "Fondator", emoji: "🏛️", color: "from-[#D4A63D] to-[#9C7C26]" },
  "expert-verificat": { name: "Expert verificat", emoji: "✔️", color: "from-[#5FA0E8] to-[#3F7EC8]" },
  "storyteller-verificat": { name: "Storyteller Verificat", emoji: "📖", color: "from-[#9C8FE0] to-[#7163C0]" },
  "vocea-schimbarii": { name: "Vocea Schimbării", emoji: "📢", color: "from-[#E89BB8] to-[#D4748E]" },
  "lider-regional": { name: "Lider Regional", emoji: "🏛️", color: "from-[#F2C053] to-[#D4A63D]" },
  "sustinator-financiar": { name: "Susținător Financiar", emoji: "💎", color: "from-[#5FA0E8] to-[#3F7EC8]" },
  "viral-ambassador": { name: "Ambasador Viral", emoji: "🌐", color: "from-[#3BA76D] to-[#2D8557]" },
};

const ACTION_LABELS = {
  petition_sign: "Petiție semnată",
  story_submit: "Poveste împărtășită",
  story_published: "Poveste publicată",
  comment_post: "Comentariu postat",
  comment_helpful: "Comentariu util",
  article_share: "Articol distribuit",
  donation_min: "Donație",
  donation_100plus: "Donație 100+ RON",
  referral_signed: "Prieten invitat a semnat",
};

export default function Profile() {
  const { user, loading, logout } = useAuth() || {};
  const [rep, setRep] = useState(null);
  const [quote, setQuote] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [savingQuote, setSavingQuote] = useState(false);

  const loadRep = () => {
    api.get("/reputation/me").then((r) => {
      setRep(r.data);
      // Lazy-load champion-quote fields from user profile (re-fetch user)
      if (r.data?.authenticated) {
        api.get("/auth/me").then((u) => {
          setQuote(u.data?.public_quote || "");
          setDisplayName(u.data?.public_display_name || "");
        }).catch(() => {});
      }
    }).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    loadRep();
  }, [user]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-32 text-[#A0AABF]">Se încarcă...</div>;
  if (!user) return <Navigate to="/autentificare" replace />;

  const points = rep?.points ?? user.reputation_points ?? 0;
  const level = rep?.level ?? user.level ?? 1;
  const nextAt = rep?.next_level_at;
  const badges = rep?.badges ?? user.badges ?? [];
  const log = rep?.log ?? [];
  const refCode = rep?.ref_code;
  const progressToNext = nextAt ? Math.min(100, (points / nextAt) * 100) : 100;
  const isAmbassador = points >= 400;

  const refLink = refCode
    ? `${window.location.origin}/?ref=${refCode}`
    : null;

  const copyRefLink = () => {
    if (!refLink) return;
    navigator.clipboard.writeText(refLink);
    toast.success("Link-ul tău de invitație a fost copiat!");
  };

  const shareRefLink = () => {
    if (!refLink) return;
    const text = `Susțin TatiSiSofia.ro — platformă națională pentru copiii separați de un părinte iubitor. Semnează prin link-ul meu:`;
    if (navigator.share) {
      navigator.share({ title: "TatiSiSofia.ro", text, url: refLink }).catch(() => {});
    } else {
      copyRefLink();
    }
  };

  const saveQuote = async () => {
    setSavingQuote(true);
    try {
      await api.post("/champions/quote", {
        quote: quote.trim().slice(0, 240),
        public_display_name: displayName.trim().slice(0, 60) || null,
      });
      toast.success("Citatul tău public a fost salvat!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSavingQuote(false);
    }
  };

  return (
    <div data-testid="page-profile" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <header className="flex items-center gap-5 mb-10">
        <div className="w-20 h-20 rounded-full bg-[#1A2340] border border-[#D4A63D]/30 grid place-items-center text-3xl text-[#D4A63D] font-display">
          {user.avatar_url ? <img src={user.avatar_url} alt={user.name} className="w-full h-full rounded-full object-cover" /> : user.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1">
          <h1 className="font-display text-3xl md:text-4xl text-[#F8F8F4]">{user.name}</h1>
          <p className="text-sm text-[#A0AABF]">{user.email}</p>
          {user.county && <p className="text-xs text-[#6C7A9C] mt-1">{user.county}</p>}
        </div>
      </header>

      {/* REPUTATION + LEVEL */}
      <div className="cinematic-card rounded-lg p-6 mb-6 relative overflow-hidden" data-testid="profile-reputation">
        <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full opacity-15 pointer-events-none blur-3xl"
             style={{ background: "radial-gradient(circle, #D4A63D 0%, transparent 70%)" }} />
        <div className="relative grid sm:grid-cols-3 gap-5 items-center">
          <div className="text-center sm:text-left">
            <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#D4A63D] mb-1">Reputație</div>
            <div className="font-display text-5xl gold-text leading-none flex items-baseline gap-2">
              {points.toLocaleString("ro-RO")}
              <span className="text-sm text-[#A0AABF]">pct</span>
            </div>
          </div>
          <div className="text-center">
            <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#E89BB8] mb-1">Nivel</div>
            <div className="font-display text-4xl text-[#F8F8F4] flex items-center justify-center gap-2">
              <Crown className="w-6 h-6 text-[#D4A63D]" />
              {level}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#A0AABF] mb-1">
              {nextAt ? `Spre nivel ${level + 1}` : "Nivel maxim atins"}
            </div>
            <div className="h-2 bg-[#0A1020] rounded-full overflow-hidden border border-white/5">
              <div className="h-full bg-gradient-to-r from-[#D4A63D] to-[#E89BB8] transition-all duration-700" style={{ width: `${progressToNext}%` }} />
            </div>
            {nextAt && (
              <div className="text-[10px] text-[#6C7A9C] mt-1.5 font-mono">{points} / {nextAt} puncte</div>
            )}
          </div>
        </div>
      </div>

      {/* BADGES */}
      {badges.length > 0 && (
        <div className="cinematic-card rounded-lg p-5 mb-6" data-testid="profile-badges">
          <h3 className="text-xs uppercase tracking-widest text-[#A0AABF] mb-4 flex items-center gap-2">
            <Award className="w-4 h-4 text-[#D4A63D]" /> Badge-uri câștigate ({badges.length})
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {badges.map((b) => {
              const meta = BADGE_META[b] || { name: b, emoji: "🏅", color: "from-white/10 to-white/5" };
              return (
                <div key={b} data-testid={`badge-${b}`} className={`relative rounded-sm p-3 bg-gradient-to-br ${meta.color} border border-white/15 text-center`}>
                  <div className="text-2xl mb-1">{meta.emoji}</div>
                  <div className="text-[11px] font-bold text-[#0A1020] uppercase tracking-wide">{meta.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ACTIVITY LOG */}
      {log.length > 0 && (
        <div className="cinematic-card rounded-lg p-5 mb-6" data-testid="profile-activity">
          <h3 className="text-xs uppercase tracking-widest text-[#A0AABF] mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#D4A63D]" /> Activitate recentă
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {log.slice(0, 20).map((l) => (
              <div key={l.log_id} className="flex items-center justify-between text-[12.5px] py-2 px-3 bg-[#0A1020]/40 rounded-sm border border-white/5">
                <div className="min-w-0 flex-1">
                  <div className="text-[#F8F8F4] truncate">{l.reason || ACTION_LABELS[l.action] || l.action}</div>
                  <div className="text-[10px] text-[#6C7A9C] mt-0.5">{new Date(l.created_at).toLocaleDateString("ro-RO", { day: "numeric", month: "short", year: "numeric" })}</div>
                </div>
                <div className="text-[#D4A63D] font-mono text-[12px] shrink-0 ml-3">+{l.points}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SHARE-TO-EARN: invite friends */}
      {refCode && (
        <div className="cinematic-card rounded-lg p-5 mb-6" data-testid="profile-referral">
          <h3 className="text-xs uppercase tracking-widest text-[#A0AABF] mb-3 flex items-center gap-2">
            <Share2 className="w-4 h-4 text-[#D4A63D]" /> Invită prieteni · Câștigă puncte
          </h3>
          <p className="text-[13px] text-[#A0AABF] leading-relaxed mb-3">
            Distribuie link-ul tău unic. Pentru fiecare prieten care semnează o petiție prin link-ul tău primești <strong className="text-[#D4A63D]">+5 puncte</strong>. La 5 prieteni invitați primești badge-ul <strong className="text-[#E89BB8]">Ambasador Viral</strong>.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="profile-ref-link"
              readOnly
              value={refLink || ""}
              onClick={(e) => e.target.select()}
              className="flex-1 bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-[12px] text-[#F8F8F4] font-mono focus:border-[#D4A63D] outline-none"
            />
            <button
              type="button"
              onClick={copyRefLink}
              data-testid="profile-ref-copy"
              className="btn-gold-outline rounded-sm px-4 py-2.5 text-[11px] font-bold tracking-widest inline-flex items-center justify-center gap-2"
            >
              <Copy className="w-3.5 h-3.5" /> COPIAZĂ
            </button>
            <button
              type="button"
              onClick={shareRefLink}
              data-testid="profile-ref-share"
              className="btn-gold rounded-sm px-4 py-2.5 text-[11px] font-bold tracking-widest inline-flex items-center justify-center gap-2"
            >
              <Share2 className="w-3.5 h-3.5" /> DISTRIBUIE
            </button>
          </div>
          <p className="text-[10px] text-[#6C7A9C] mt-2 font-mono">Codul tău: <strong className="text-[#D4A63D]">{refCode}</strong></p>
        </div>
      )}

      {/* CHAMPION QUOTE EDITOR (Ambassadors+) */}
      {isAmbassador && (
        <div className="cinematic-card rounded-lg p-5 mb-6 border-[#E89BB8]/25" data-testid="profile-quote-editor">
          <h3 className="text-xs uppercase tracking-widest text-[#E89BB8] mb-3 flex items-center gap-2">
            <Quote className="w-4 h-4 text-[#E89BB8]" /> Wall of Champions · Editor public
          </h3>
          <p className="text-[12.5px] text-[#A0AABF] leading-relaxed mb-3">
            Ești în top {points >= 1000 ? "Lider de comunitate" : "Ambasador"}. Poți seta un nume public și un citat scurt care apar pe pagina principală.
          </p>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Nume afișat public (lasă gol pentru numele real)"
            maxLength={60}
            data-testid="profile-quote-name"
            className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#E89BB8] outline-none mb-2"
          />
          <textarea
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            placeholder="Citatul tău public (max 240 caractere) — un mesaj scurt pentru oamenii care vor vedea profilul tău pe Wall of Champions"
            maxLength={240}
            rows={3}
            data-testid="profile-quote-text"
            className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#E89BB8] outline-none resize-none mb-3"
          />
          <div className="flex items-center justify-between text-[11px] text-[#6C7A9C]">
            <span>{quote.length}/240 caractere</span>
            <button
              type="button"
              onClick={saveQuote}
              disabled={savingQuote}
              data-testid="profile-quote-save"
              className="btn-gold rounded-sm px-4 py-2 text-[10.5px] font-bold tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" /> {savingQuote ? "SE SALVEAZĂ..." : "SALVEAZĂ"}
            </button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <Link to="/spune-povestea" className="cinematic-card rounded-lg p-5 group">
          <div className="text-xs uppercase tracking-widest text-[#D4A63D] mb-1">Acțiune · +25 pct</div>
          <div className="font-display text-lg text-[#F8F8F4] group-hover:text-[#D4A63D]">Spune povestea ta</div>
        </Link>
        <Link to="/petitii" className="cinematic-card rounded-lg p-5 group">
          <div className="text-xs uppercase tracking-widest text-[#D4A63D] mb-1">Activism · +10 pct</div>
          <div className="font-display text-lg text-[#F8F8F4] group-hover:text-[#D4A63D]">Semnează o petiție</div>
        </Link>
      </div>

      {user.role === "admin" && (
        <Link to="/admin" data-testid="profile-admin-link" className="cinematic-card rounded-lg p-5 block mb-6 border-[#D4A63D]/30">
          <div className="text-xs uppercase tracking-widest text-[#D4A63D] mb-1">Administrare</div>
          <div className="font-display text-lg text-[#F8F8F4]">Panou Admin →</div>
        </Link>
      )}

      <button data-testid="profile-logout" onClick={logout} className="text-[#C83A3A] hover:text-[#F2C053] text-sm inline-flex items-center gap-2">
        <LogOut className="w-4 h-4" /> Ieși din cont
      </button>
    </div>
  );
}
